import { onBeforeUnmount, ref } from 'vue';
import { completeExternalAuth, startExternalAuth } from '@/api/auth';
import type { ExternalAuthMessage, ExternalAuthResult, ExternalProvider } from '@/typings/auth/externalAuth';

const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 700;

/**
 * Origin of the callback page (hosted in the Shopify app), read from the redirect_uri our backend put in the
 * authorization url: providers may land on different hosts locally (http localhost vs an https tunnel).
 */
const callbackOriginOf = (authorizationUrl: string): string => {
    const redirectUri = new URL(authorizationUrl).searchParams.get('redirect_uri');
    return new URL(redirectUri || (process.env.VUE_APP_SHOPIFY_APP_URL as string)).origin;
};

const openCenteredPopup = (): Window | null => {
    const left = window.screenX + (window.outerWidth - POPUP_WIDTH) / 2;
    const top = window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2;

    // Opened empty on the click itself, otherwise the browser blocks it while we ask for the url
    return window.open(
        'about:blank',
        'customily_external_auth',
        `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top}`
    );
};

const CANCELLED_MESSAGE = 'The sign in was cancelled.';
// Provider error codes are not worded for users, and CError renders its message as HTML
const PROVIDER_FAILED_MESSAGE = 'The provider could not complete the sign in. Please try again.';

/** Returned by a result handler that is leaving the page, so the buttons stay disabled until it does. */
export type AuthResultHandled = void | 'navigating';

// What the provider sends back when the user declines the consent screen
const PROVIDER_CANCELLED_ERRORS = ['access_denied', 'user_cancelled_login', 'user_cancelled_authorize'];

export const useExternalAuth = () => {
    const loadingProvider = ref<ExternalProvider | null>(null);
    // Only the current attempt, so a new one can be told apart from the leftovers of the last one
    let popup: Window | null = null;
    let stopWaiting: (() => void) | null = null;

    /** Resolves with the callback of this popup only, and stops watching it once settled either way. */
    const waitForCallback = (
        authPopup: Window,
        provider: ExternalProvider,
        callbackOrigin: string
    ): Promise<ExternalAuthMessage> =>
        new Promise((resolve, reject) => {
            const listener = (event: MessageEvent<ExternalAuthMessage>) => {
                if (event.origin !== callbackOrigin || event.source !== authPopup) return;
                if (event.data?.action !== 'external-auth-completed' || event.data.provider !== provider) return;

                cleanUp();
                authPopup.close();

                const { error, code } = event.data;
                if (error || !code) {
                    reject(
                        new Error(
                            !error || PROVIDER_CANCELLED_ERRORS.includes(error)
                                ? CANCELLED_MESSAGE
                                : PROVIDER_FAILED_MESSAGE
                        )
                    );
                    return;
                }
                resolve(event.data);
            };

            const closedCheck = window.setInterval(() => {
                if (authPopup.closed) {
                    cleanUp();
                    reject(new Error(CANCELLED_MESSAGE));
                }
            }, 500);

            const cleanUp = () => {
                window.removeEventListener('message', listener);
                window.clearInterval(closedCheck);
                stopWaiting = null;
            };

            stopWaiting = cleanUp;
            window.addEventListener('message', listener);
        });

    /**
     * Runs the whole popup round trip and hands what the backend decided (a session ticket, a checkout, or
     * the extra data the sign up still needs) to `onResult`. The buttons stay disabled until `onResult` is
     * done, so a second click cannot start another popup while the session is being opened.
     */
    const authenticate = async (
        provider: ExternalProvider,
        intent: 'signup' | 'signin',
        options: { storeType?: string; managedBy?: string },
        onResult: (result: ExternalAuthResult) => Promise<AuthResultHandled>
    ): Promise<void> => {
        if (loadingProvider.value) return;

        loadingProvider.value = provider;
        const authPopup = openCenteredPopup();
        popup = authPopup;
        let navigating = false;

        try {
            if (!authPopup) {
                throw new Error('Please allow pop-ups for this site to continue.');
            }

            const authorizationUrl = await startExternalAuth(provider, intent, options.storeType, options.managedBy);
            authPopup.location.href = authorizationUrl;
            const callback = await waitForCallback(authPopup, provider, callbackOriginOf(authorizationUrl));
            navigating =
                (await onResult(await completeExternalAuth(provider, callback.code, callback.state))) === 'navigating';
        } finally {
            stopWaiting?.();
            // Covers every failure before the callback, where the popup would otherwise stay open and blank
            if (authPopup && !authPopup.closed) authPopup.close();
            popup = null;
            if (!navigating) loadingProvider.value = null;
        }
    };

    onBeforeUnmount(() => {
        stopWaiting?.();
        popup?.close();
    });

    return { authenticate, loadingProvider };
};
